import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { Avatar, Dropdown, Icon, LogoIcon } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { ClubFilterProvider, useClubFilter } from "@/lib/clubs/club-filter";
import { CLUB_DOT_COLORS, ClubsProvider, useClubs } from "@/lib/clubs/clubs";
import { useDensity } from "@/lib/prefs/density";
import { cn } from "@/lib/utils";
import { NotificationsBell } from "./NotificationsBell";
import { initials, LangToggle, SidebarNav, UserMenu, type NavGroupDef } from "./parts";

/**
 * Back-Office (TC staff) shell (architecture §3.3 / §4.3): cross-club operator chrome —
 * operator sidebar plus a topbar club-context switcher and global search. Ported from the
 * prototype's tc-shell.jsx. Mounted for BACK_OFFICE accounts only.
 *
 * Nav labels and chrome are i18n keys (TW-3434) resolved live by the language toggle.
 */
const NAV: NavGroupDef[] = [
  {
    items: [
      { to: "/", icon: "dashboard", label: "nav.dashboard", end: true },
      { to: "/requests", icon: "bell", label: "nav.requests" },
      { to: "/trips", icon: "trips", label: "nav.trips" },
      { to: "/calendar", icon: "calendar", label: "nav.calendar" },
    ],
  },
  {
    label: "nav.groupClubs",
    items: [
      { to: "/clubs", icon: "building", label: "nav.clubs" },
      { to: "/roster", icon: "roster", label: "nav.roster" },
      { to: "/messages", icon: "messages", label: "nav.messages" },
      { to: "/invoices", icon: "invoices", label: "nav.invoices" },
      { to: "/emails", icon: "mail", label: "nav.emails" },
    ],
  },
  {
    items: [{ to: "/settings", icon: "settings", label: "nav.settings" }],
  },
];

/**
 * Topbar club-context switcher, backed by the real clubs endpoint (GET /api/clubs via
 * ClubsProvider) and now wired to the shared ClubFilterProvider (TW-3440): picking a club here
 * scopes every ops surface (dashboard, requests, all-trips) to that club via `?clubId=`. "All
 * clubs" is the cross-club default for back-office staff.
 */
function ClubSwitcher({ allClubsLabel }: { allClubsLabel: string }) {
  const { clubs } = useClubs();
  const { clubId, setClubId } = useClubFilter();
  const current = clubs.find((c) => c.id === clubId);
  const dotColor = (id: string) =>
    CLUB_DOT_COLORS[clubs.findIndex((c) => c.id === id) % CLUB_DOT_COLORS.length];

  return (
    <Dropdown
      align="left"
      trigger={
        <span className="club-switch">
          {current ? (
            <span className="club-dot" style={{ background: dotColor(current.id) }} />
          ) : (
            <Icon name="globe" size={16} className="muted" />
          )}
          <span>{current ? current.name : allClubsLabel}</span>
          <Icon name="chevD" size={15} className="faint" />
        </span>
      }
    >
      <button
        type="button"
        className={cn("dropdown-item", clubId === "all" && "active")}
        onClick={() => setClubId("all")}
      >
        <Icon name="globe" size={16} /> {allClubsLabel}
      </button>
      <div className="dropdown-sep" />
      {clubs.map((c) => (
        <button
          key={c.id}
          type="button"
          className={cn("dropdown-item", clubId === c.id && "active")}
          onClick={() => setClubId(c.id)}
        >
          <span className="club-dot" style={{ background: dotColor(c.id) }} /> {c.name}
        </button>
      ))}
    </Dropdown>
  );
}

export function BackOfficeLayout() {
  const { t } = useTranslation();
  const { me } = useAuthz();

  const name = me?.name ?? "—";
  const role = me?.roles[0] ?? t("shell.backOffice");
  const { density } = useDensity();

  return (
    <ClubsProvider>
      <ClubFilterProvider>
        <div className={cn("app", density === "compact" && "dense")}>
          <aside className="sidebar">
            <div className="brand">
              <div className="brand-mark ops">
                <LogoIcon style={{ height: 30 }} />
              </div>
              <div>
                <div className="brand-name">Travel Connection</div>
                <div className="brand-sub">{t("shell.operations")}</div>
              </div>
            </div>

            <SidebarNav groups={NAV} />

            <div className="sidebar-foot">
              <div className="club-card" style={{ cursor: "default" }}>
                <Avatar initials={initials(name)} src={me?.image} size={38} />
                <div className="meta">
                  <b>{name}</b>
                  <span>{role}</span>
                </div>
              </div>
            </div>
          </aside>

          <div className="main">
            <header className="topbar">
              <div className="topbar-spacer" />
              <ClubSwitcher allClubsLabel={t("shell.allClubs")} />
              <LangToggle />
              <NotificationsBell />
              <UserMenu name={name} role={role} image={me?.image} />
            </header>

            <div className="content">
              <Outlet />
            </div>
          </div>
        </div>
      </ClubFilterProvider>
    </ClubsProvider>
  );
}
